import { useEffect, useRef, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	LinearProgress,
	Skeleton,
	Stack,
	Typography,
	useMediaQuery,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { getTryoutSummaryAnalysis } from "src/services/tryout.service";

export const TRYOUT_OPEN_SUMMARY_ANALYSIS_EVENT = "tryout-open-summary-analysis";

type AnalysisItem = {
	category?: string;
	title: string;
	description: string;
};

type RecommendationItem = {
	title: string;
	description: string;
};

export type SummaryAnalysisData = {
	strengths: AnalysisItem[];
	weaknesses: AnalysisItem[];
	learning_recommendations: RecommendationItem[];
};

type Status = "idle" | "loading" | "success" | "error";

type SummaryAnalysisProps = {
	tryoutId: string;
	tries: string | number;
};

const parseAnalysisPayload = (payload: unknown): SummaryAnalysisData | null => {
	if (!payload || typeof payload !== "object") return null;

	const root = payload as Record<string, unknown>;
	const candidate =
		root.strengths || root.weaknesses || root.learning_recommendations
			? root
			: (root.data as Record<string, unknown> | undefined);

	if (!candidate || typeof candidate !== "object") return null;

	const nested =
		candidate.strengths || candidate.weaknesses || candidate.learning_recommendations
			? candidate
			: (candidate.data as Record<string, unknown> | undefined);

	if (!nested || typeof nested !== "object") return null;

	return {
		strengths: Array.isArray(nested.strengths) ? (nested.strengths as AnalysisItem[]) : [],
		weaknesses: Array.isArray(nested.weaknesses) ? (nested.weaknesses as AnalysisItem[]) : [],
		learning_recommendations: Array.isArray(nested.learning_recommendations)
			? (nested.learning_recommendations as RecommendationItem[])
			: [],
	};
};

const sectionHeaderSx = {
	fontWeight: 700,
	letterSpacing: "0.04em",
	textTransform: "uppercase" as const,
	mb: 1.5,
};

const loadingStages = [
	"Membaca hasil tryout terakhir",
	"Memetakan kekuatan dan kelemahan",
	"Menyusun rekomendasi belajar",
];

const PHASE_1_MS = 4000;
const PHASE_2_MS = 4000;
const TOTAL_PROGRESS_MS = 30000;
const MAX_PENDING_PROGRESS = 95;

const listItemSx = (index: number, reduceMotion: boolean) => ({
	mb: 1.5,
	"&:last-child": { mb: 0 },
	...(reduceMotion
		? {}
		: {
			animation: "summaryFadeIn 280ms ease-out both",
			animationDelay: `${index * 60}ms`,
			"@keyframes summaryFadeIn": {
				from: { opacity: 0, transform: "translateY(8px)" },
				to: { opacity: 1, transform: "translateY(0)" },
			},
		}),
});

const SummaryAnalysis = ({ tryoutId, tries }: SummaryAnalysisProps) => {
	const [open, setOpen] = useState(false);
	const [status, setStatus] = useState<Status>("idle");
	const [data, setData] = useState<SummaryAnalysisData | null>(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [loadingStageIndex, setLoadingStageIndex] = useState(0);
	const [loadingProgress, setLoadingProgress] = useState(0);
	const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

	useEffect(() => {
		setOpen(false);
		setStatus("idle");
		setData(null);
		setErrorMessage("");
		setLoadingStageIndex(0);
		setLoadingProgress(0);
	}, [tryoutId, tries]);

	useEffect(() => {
		if (!(open && status === "loading")) {
			return;
		}

		const startedAt = Date.now();
		setLoadingStageIndex(0);
		setLoadingProgress(0);

		const tick = () => {
			const elapsed = Date.now() - startedAt;

			if (elapsed < PHASE_1_MS) {
				setLoadingStageIndex(0);
			} else if (elapsed < PHASE_1_MS + PHASE_2_MS) {
				setLoadingStageIndex(1);
			} else {
				setLoadingStageIndex(2);
			}

			const rawProgress = (elapsed / TOTAL_PROGRESS_MS) * 100;
			setLoadingProgress(Math.min(MAX_PENDING_PROGRESS, Math.max(0, rawProgress)));
		};

		tick();
		const intervalId = window.setInterval(tick, 100);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [open, status]);

	const handleClose = () => {
		if (status === "loading") return;
		setOpen(false);
	};

	const fetchAnalysis = async () => {
		if (!tryoutId || tries === undefined || tries === null || tries === "") {
			setOpen(true);
			setStatus("error");
			setErrorMessage("Data percobaan tidak tersedia.");
			return;
		}

		setOpen(true);
		setStatus("loading");
		setErrorMessage("");
		setData(null);

		try {
			const response = await getTryoutSummaryAnalysis(String(tryoutId), String(tries));
			const parsed = parseAnalysisPayload(response?.data);

			if (!parsed) {
				throw new Error("Format respons analisa tidak valid.");
			}

			setLoadingProgress(100);
			setLoadingStageIndex(2);
			setData(parsed);
			setStatus("success");
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				error?.message ||
				"Gagal memuat analisa. Silakan coba lagi.";
			setErrorMessage(message);
			setStatus("error");
			setData(null);
			setLoadingProgress(0);
			setLoadingStageIndex(0);
		}
	};

	const fetchAnalysisRef = useRef(fetchAnalysis);
	fetchAnalysisRef.current = fetchAnalysis;

	useEffect(() => {
		const handleExternalOpen = () => {
			fetchAnalysisRef.current();
		};
		window.addEventListener(TRYOUT_OPEN_SUMMARY_ANALYSIS_EVENT, handleExternalOpen);
		return () => {
			window.removeEventListener(TRYOUT_OPEN_SUMMARY_ANALYSIS_EVENT, handleExternalOpen);
		};
	}, []);

	const renderSkeleton = () => (
		<Box aria-busy="true" aria-live="polite">
			<Box
				sx={(theme) => ({
					mb: 2,
					p: 2,
					borderRadius: 2,
					border: "1px solid",
					borderColor: theme.colors.primary.light,
					bgcolor: theme.colors.primary.lighter,
				})}
			>
				<Stack spacing={1.25}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={1}
						alignItems={{ xs: "flex-start", sm: "center" }}
						justifyContent="space-between"
					>
						<Stack direction="row" spacing={1} alignItems="center">
							<CircularProgress size={18} />
							<Typography variant="subtitle2" fontWeight={700}>
								Brodi sedang menganalisa pembahasanmu
							</Typography>
						</Stack>
						<Typography variant="caption" color="text.secondary">
							Tahap {loadingStageIndex + 1} dari {loadingStages.length} · {Math.round(loadingProgress)}%
						</Typography>
					</Stack>

					<LinearProgress
						variant="determinate"
						value={loadingProgress}
						color="primary"
						sx={{
							height: 8,
							borderRadius: 999,
							"& .MuiLinearProgress-bar": {
								borderRadius: 999,
								...(reduceMotion ? {} : { transition: "transform 120ms linear" }),
							},
						}}
					/>

					<Typography variant="body2" color="text.primary" fontWeight={600}>
						{loadingStages[loadingStageIndex]}
					</Typography>
				</Stack>
			</Box>

			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Box
						sx={(theme) => ({
							p: 2,
							borderRadius: 2,
							bgcolor: theme.colors.success.lighter,
							border: "1px solid",
							borderColor: theme.colors.success.light,
						})}
					>
						<Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
						<Skeleton variant="text" width="82%" />
						<Skeleton variant="text" width="92%" />
						<Skeleton variant="text" width="68%" />
					</Box>
				</Grid>
				<Grid item xs={12} md={6}>
					<Box
						sx={(theme) => ({
							p: 2,
							borderRadius: 2,
							bgcolor: theme.colors.error.lighter,
							border: "1px solid",
							borderColor: theme.colors.error.light,
						})}
					>
						<Skeleton variant="text" width="50%" sx={{ mb: 1 }} />
						<Skeleton variant="text" width="88%" />
						<Skeleton variant="text" width="85%" />
						<Skeleton variant="text" width="60%" />
					</Box>
				</Grid>
				<Grid item xs={12}>
					<Box
						sx={{
							p: 2,
							borderRadius: 2,
							bgcolor: "grey.100",
							border: "1px solid",
							borderColor: "grey.300",
						}}
					>
						<Skeleton variant="text" width="32%" sx={{ mb: 1 }} />
						<Skeleton variant="text" width="94%" />
						<Skeleton variant="text" width="90%" />
						<Skeleton variant="text" width="78%" />
					</Box>
				</Grid>
			</Grid>
		</Box>
	);

	const renderPanel = (analysis: SummaryAnalysisData) => (
		<Box
			sx={{
				...(reduceMotion
					? {}
					: {
						animation: "summaryPanelIn 260ms ease-out",
						"@keyframes summaryPanelIn": {
							from: { opacity: 0, transform: "translateY(10px)" },
							to: { opacity: 1, transform: "translateY(0)" },
						},
					}),
			}}
		>
			<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
				Dihasilkan oleh Brodi
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Box
						sx={(theme) => ({
							p: 2,
							height: "100%",
							borderRadius: 1,
							bgcolor: theme.colors.success.lighter,
							border: "1px solid",
							borderColor: theme.colors.success.light,
						})}
					>
						<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
							<CheckCircleOutlineIcon color="success" fontSize="small" />
							<Typography
								variant="subtitle2"
								sx={{ ...sectionHeaderSx, color: "success.dark", mb: 0 }}
							>
								Kekuatan
							</Typography>
						</Stack>
						{analysis.strengths.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								Belum ada kekuatan yang teridentifikasi untuk percobaan ini.
							</Typography>
						) : (
							analysis.strengths.map((item, index) => (
								<Box key={`strength-${item.title}-${index}`} sx={listItemSx(index, reduceMotion)}>
									<Typography variant="subtitle1" fontWeight={700} color="text.primary">
										{item.title}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{item.description}
									</Typography>
								</Box>
							))
						)}
					</Box>
				</Grid>

				<Grid item xs={12} md={6}>
					<Box
						sx={(theme) => ({
							p: 2,
							height: "100%",
							borderRadius: 1,
							bgcolor: theme.colors.error.lighter,
							border: "1px solid",
							borderColor: theme.colors.error.light,
						})}
					>
						<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
							<PriorityHighIcon color="error" fontSize="small" />
							<Typography
								variant="subtitle2"
								sx={{ ...sectionHeaderSx, color: "error.dark", mb: 0 }}
							>
								Perlu Ditingkatkan
							</Typography>
						</Stack>
						{analysis.weaknesses.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								Tidak ada area yang perlu ditingkatkan secara signifikan.
							</Typography>
						) : (
							analysis.weaknesses.map((item, index) => (
								<Box key={`weakness-${item.title}-${index}`} sx={listItemSx(index, reduceMotion)}>
									<Typography variant="subtitle1" fontWeight={700} color="text.primary">
										{item.title}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{item.description}
									</Typography>
								</Box>
							))
						)}
					</Box>
				</Grid>

				<Grid item xs={12}>
					<Accordion
						defaultExpanded
						disableGutters
						sx={{
							bgcolor: "grey.100",
							border: "1px solid #eaeaea",
							borderRadius: "8px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 48,
								"& .MuiAccordionSummary-content": { my: 1.5 },
							}}
						>
							<Typography variant="subtitle1" fontWeight={700}>
								Rekomendasi belajar
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 2 }}>
							{analysis.learning_recommendations.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									Belum ada rekomendasi belajar untuk percobaan ini.
								</Typography>
							) : (
								<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
									{analysis.learning_recommendations.map((item, index) => (
										<Box
											component="li"
											key={`rec-${item.title}-${index}`}
											sx={{
												mb: 1.25,
												"&:last-child": { mb: 0 },
												...(reduceMotion
													? {}
													: {
														animation: "summaryFadeIn 280ms ease-out both",
														animationDelay: `${index * 60}ms`,
														"@keyframes summaryFadeIn": {
															from: { opacity: 0, transform: "translateY(8px)" },
															to: { opacity: 1, transform: "translateY(0)" },
														},
													}),
											}}
										>
											<Typography variant="subtitle1" fontWeight={700} component="span">
												{item.title}
											</Typography>
											<Typography variant="body2" color="text.secondary">
												{item.description}
											</Typography>
										</Box>
									))}
								</Box>
							)}
						</AccordionDetails>
					</Accordion>
				</Grid>
			</Grid>
		</Box>
	);

	return (
		<Box id="tryout-evaluasi" sx={{ mt: 3 }}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "stretch", sm: "center" }}
				spacing={1.5}
				flexWrap="wrap"
			>
				<Button
					variant="contained"
					color="primary"
					size="small"
					onClick={fetchAnalysis}
					disabled={status === "loading" && open}
					startIcon={
						status === "loading" && open ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							<AutoAwesomeIcon />
						)
					}
					sx={{
						minHeight: 44,
						cursor: status === "loading" && open ? "default" : "pointer",
						transition: "background-color 200ms ease, box-shadow 200ms ease",
					}}
				>
					{status === "loading" && open ? "Menganalisa..." : "Analisa menggunakan Brodi"}
				</Button>
				<Typography variant="body2" color="text.secondary">
					Dapatkan ringkasan kelebihan &amp; kelemahan
				</Typography>
			</Stack>

			<Dialog
				open={open}
				onClose={handleClose}
				fullWidth
				maxWidth="md"
				scroll="paper"
				aria-labelledby="summary-analysis-dialog-title"
			>
				<DialogTitle
					id="summary-analysis-dialog-title"
					sx={{ pr: 6, display: "flex", alignItems: "center", gap: 1 }}
				>
					<AutoAwesomeIcon color="primary" fontSize="small" />
					Summary Pembahasan
					<IconButton
						aria-label="Tutup"
						onClick={handleClose}
						disabled={status === "loading"}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Alert
						severity="info"
						variant="filled"
						icon={<InfoOutlinedIcon fontSize="inherit" />}
						sx={{ mb: 2, color: "text.primary" }}
					>
						Jumlah soal yang disebutkan dalam analisa (misalnya &quot;21 dari 25 soal&quot;)
						merujuk pada soal di <strong>masing-masing kategori</strong>, bukan total
						keseluruhan soal tryout.
					</Alert>

					{status === "loading" && renderSkeleton()}

					{status === "error" && (
						<Alert
							severity="error"
							action={
								<Button color="inherit" size="small" onClick={fetchAnalysis}>
									Coba lagi
								</Button>
							}
						>
							{errorMessage}
						</Alert>
					)}

					{status === "success" && data && renderPanel(data)}
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button onClick={handleClose} disabled={status === "loading"} variant="outlined">
						Tutup
					</Button>
					{status === "error" && (
						<Button onClick={fetchAnalysis} variant="contained" startIcon={<AutoAwesomeIcon />}>
							Coba lagi
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default SummaryAnalysis;
